import React, { useState } from 'react';
import { bool, string, func } from 'prop-types';
import Input from './Input';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { LazyLoadImage } from 'react-lazy-load-image-component';

const PasswordInput = ({
  autoComplete,
  name,
  password,
  onInputChange,
  onInputBlur,
  error,
  parentClass,
  label,
  showInfoIcon,
  showConfirmMatch,
  autoFocus,
  passwordPlaceholder,
  isFromSignupFlow,
  confirmPasswordErr,
  apiCommonValidationError
}) => {
  const [showPassword, setVisibility] = useState(true);
  const [isHoverActive, setIsHoverActive] = useState(false);
  const { t } = useTranslation();

  const toggleTooltip = e => {
    if (e.key === 'Enter' || e.key === ' ')
      document
        .getElementById('password-tooltip')
        .classList.add('visibleTooltip');
    else
      document
        .getElementById('password-tooltip')
        .classList.remove('visibleTooltip');
  };

  const handleKeyDownForEyeIcon = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setVisibility(!showPassword);
    }
  };

  return (
    <div className={`${parentClass} inputWrapper`}>
      <label id={`${name}-field`}>
        {t(label)}
        {name === 'password' && showInfoIcon && (
          <div onMouseLeave={() => setIsHoverActive(false)}>
            <span
              className="dob-info d-inline-block dialogComponent"
              tabIndex={0}
              onKeyDown={toggleTooltip}
              onBlur={toggleTooltip}
              onMouseEnter={() => setIsHoverActive(true)}
              aria-describedby="password-tooltip"
              role="tooltip"
            >
              {t('i')}
            </span>
            <div
              className={`show-tooltip ${
                isHoverActive ? 'visibleTooltip' : ''
              }`}
              id="password-tooltip"
              onMouseEnter={() => setIsHoverActive(true)}
              onMouseLeave={() => setIsHoverActive(false)}
            >
              <p>
                {t('Passwords must have')} <br />• {t('at least 8 characters')}{' '}
                <br />• {t('at least 1 number')} <br />•{' '}
                {t('at least 1 capital letter')}
                <br />• {t('at least 1 lowercase letter')} <br />•{' '}
                {t('at least 1 special letter')}
                <br />• {t('no spaces')}
                <br />• {t('no common phrases')} <br />
                {showConfirmMatch && `• ${t('Password confirmation matches')}`}
              </p>
              <span></span>
            </div>
          </div>
        )}
      </label>

      <div className="inputContainer">
        <input
          type={showPassword ? 'password' : ''}
          className={`dialogComponent noBorder ${error && 'invalid'}`}
          placeholder={passwordPlaceholder}
          name={name}
          value={password}
          onChange={onInputChange}
          onBlur={onInputBlur}
          autoFocus={autoFocus}
          aria-labelledby={`${name}-field`}
          aria-required="true"
          autoComplete={autoComplete}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby="passwordError"
        />
        <button
          tabIndex={0}
          type="button"
          className="eye-icon dialogComponent"
          onMouseDown={event => {
            event.preventDefault();
            setVisibility(!showPassword);
          }}
          onKeyDown={handleKeyDownForEyeIcon}
          aria-label="Preview password"
        >
          <LazyLoadImage
            alt={t('Preview password')}
            src={`/content/images/icons/${
              showPassword ? 'passwordHide' : 'passwordShow'
            }.png`}
            width="28"
            height="22"
          />
        </button>
        {error && isFromSignupFlow && _.isEmpty(confirmPasswordErr) ? (
          <ul className="errorList" id="pass_requirements">
            {apiCommonValidationError ? (
              <li>
                <span className="label-error">
                  {t('Must not be a commonly used password')}
                </span>
              </li>
            ) : (
              <React.Fragment>
                <li>
                  <span className="label-error">
                    {t('Passwords must have')}
                  </span>
                </li>
                <li>
                  <span className="label-error">
                    • {t('at least 8 characters')}
                  </span>
                </li>
                <li>
                  <span className="label-error">
                    • {t('at least 1 number')}
                  </span>
                </li>
                <li>
                  <span className="label-error">
                    • {t('at least 1 capital letter')}
                  </span>
                </li>
                <li>
                  <span className="label-error">
                    • {t('at least 1 lowercase letter')}
                  </span>
                </li>
                <li>
                  <span className="label-error">
                    • {t('at least 1 special letter')}
                  </span>
                </li>
                <li>
                  <span className="label-error">• {t('no spaces')}</span>
                </li>
              </React.Fragment>
            )}
          </ul>
        ) : (
          <span id="passwordError" className="label-error" role="alert">
            {t(error)}
          </span>
        )}
      </div>
    </div>
  );
};

PasswordInput.propTypes = {
  hidePassword: bool,
  parentClass: string,
  name: string,
  password: string,
  onInputChange: func,
  onInputBlur: func,
  error: string,
  label: string,
  showInfoIcon: bool,
  showConfirmMatch: bool,
  autoFocus: bool,
  apiCommonValidationError: bool
};

Input.defaultProps = {
  parentClass: '',
  showInfoIcon: true,
  showConfirmMatch: true,
  autoFocus: false,
  apiCommonValidationError: false
};

export default PasswordInput;
